<template>
  <div>
        <section>
                <div class="">
                    <!-- Mobile action buttons -->
                    <div class="d-flex d-md-none gap-2 mb-3" v-if="!isDetailClient">
                        <template v-if="helper.checkAccess(createPermission) != -1">
                            <button @click="create" class="btn btn-sm btn-success flex-grow-1">
                                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                                <span>{{resolvedDocumentTitle}}</span>
                            </button>
                        </template>
                        <div v-if="role === 'owner' || role === 'admin'" class="dropdown">
                            <button class="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center" type="button" data-bs-toggle="dropdown" aria-expanded="false" style="height: 31px; width: 34px; padding: 0;">
                                <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" style="font-size:14px" />
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end shadow">
                                <li>
                                    <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="toggleTrashView">
                                        <AppIcon name="bi-trash" class="bi bi-trash me-2 text-danger" />
                                        <span>{{ $t('trash') }}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="row align-items-end g-2 mb-4" v-if="!isDetailClient" :class="{'d-none d-md-flex': !showFilters}">
                        
                        <!-- Filter Dropdown (Shopify Style) -->
                        <div class="col-12 col-md-auto">
                            <div class="dropdown">
                                <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                    <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                                    <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                                </button>
                                <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                                    <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                                </button>
                                <div class="dropdown-menu p-3" aria-labelledby="filterDropdown" style="min-width: 300px;" @click.stop>
                                    
                                    <!-- Active Filters -->
                                    <div v-if="activeFilters.length > 0" class="mb-3">
                                        <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                                            <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                            <div class="input-group input-group-sm" v-if="filter.key === 'total_amount'">
                                                <select v-model="filter.operator" class="form-select" style="max-width: 80px;">
                                                    <option value="gt">{{$t('operator_gt')}}</option>
                                                    <option value="lt">{{$t('operator_lt')}}</option>
                                                    <option value="eq">{{$t('operator_eq')}}</option>
                                                </select>
                                                <input type="number" v-model="filter.value" class="form-control form-control-sm" placeholder="0">
                                                <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                            <div v-else-if="filter.key === 'client'" class="d-flex align-items-center gap-1">
                                                <div class="flex-grow-1">
                                                    <auto-complete
                                                        entity="clients"
                                                        emitEvent="emitAutoCompleteClientFilter"
                                                        :defaultValue="clientFilterName"
                                                        @emitAutoCompleteClientFilter="emitAutoCompleteClientFilter"
                                                    ></auto-complete>
                                                </div>
                                                <button class="btn btn-outline-danger btn-sm" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                            <div v-else-if="filter.key === 'order'" class="d-flex align-items-center gap-1">
                                                <div class="flex-grow-1">
                                                    <auto-complete
                                                        entity="orders"
                                                        emitEvent="emitAutoCompleteOrderFilter"
                                                        :defaultValue="orderFilterReference"
                                                        @emitAutoCompleteOrderFilter="emitAutoCompleteOrderFilter"
                                                    ></auto-complete>
                                                </div>
                                                <button class="btn btn-outline-danger btn-sm" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                            <div class="input-group input-group-sm" v-else>
                                                <select v-model="filter.value" class="form-select form-select-sm">
                                                    <option v-for="option in getFilterOptions(filter.key)" :key="option.value" :value="option.value">
                                                        {{$t(option.label)}}
                                                    </option>
                                                </select>
                                                <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                        </div>
                                        <hr class="dropdown-divider">
                                    </div>

                                    <!-- Add Filter Options -->
                                    <div class="d-grid gap-2">
                                        <button v-for="filter in selectableFilters" :key="filter.key" 
                                                class="btn btn-sm btn-outline-primary text-start" 
                                                @click="addFilter(filter)">
                                            <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                        </button>
                                    </div>

                                    <!-- Apply and Clear -->
                                    <div class="d-flex justify-content-between align-items-center mt-3">
                                        <button class="btn btn-sm btn-success" @click="applyFilters">
                                            {{$t('apply')}}
                                        </button>
                                        <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                                            {{$t('clear_all')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Spacer for desktop -->
                        <div class="col d-none d-md-block"></div>

                        <!-- Date picker -->
                        <div class="col-12 col-md-3 col-lg-2">
                            <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
                            <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                                <option value="">{{$t('filter_by_date')}}</option>
                                <option value="today">{{$t('today')}}</option>
                                <option value="yesterday">{{$t('yesterday')}}</option>
                                <option value="this_week">{{$t('this_week')}}</option>
                                <option value="last_week">{{$t('last_week')}}</option>
                                <option value="this_month">{{$t('this_month')}}</option>
                                <option value="this_year">{{$t('this_year')}}</option>
                                <option value="last_year">{{$t('last_year')}}</option>
                                <option value="custom">{{$t('custom_date')}}</option>
                            </select>
                        </div>
                        <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
                            <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
                            <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                        </div>
                        
                        <!-- Desktop action buttons -->
                        <template v-if="helper.checkAccess(createPermission) != -1">
                            <div class="col-md-2 d-none d-md-block">
                                <button @click="create" class="btn btn-sm btn-success w-100">
                                    <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                                    <span>{{resolvedDocumentTitle}}</span>
                                </button>
                            </div>
                        </template>

                    </div>

                <div class="page-table-container mb-6 bg-white rounded shadow">

                    <!-- Trash View -->
                    <div v-if="showTrash && (role === 'owner' || role === 'admin')" class="mt-5">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="mb-0"><AppIcon name="bi-trash" class="bi bi-trash me-2" />{{$t('trash')}}</h6>
                            <a href="#" class="text-decoration-none small d-flex align-items-center text-secondary" @click.prevent="toggleTrashView">
                                <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />{{$t('back')}}
                            </a>
                        </div>
                        <table-loading-bar v-if="loading" class="text-center" />
                        <div v-if="!loading" class="table-responsive">
                            <table class="table mb-0 table-borderless table-striped small">
                                <thead>
                                    <tr class="text-secondary">
                                        <th class="pt-4 pb-3 px-6">{{$t('reference')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('client')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('date')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('deleted_at')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('actions')}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in trashInvoices" :key="item.id">
                                        <td class="py-5 px-6">{{item.reference}}</td>
                                        <td class="py-5 px-6">{{item.client ? item.client.name : '-'}}</td>
                                        <td class="py-5 px-6">{{formatDate(item.date)}}</td>
                                        <td class="py-5 px-6">{{formatDate(item.deleted_at, true)}}</td>
                                        <td class="py-5 px-6">
                                            <button @click="viewRow(item)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                                <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                                                {{$t('view')}}
                                            </button>
                                            <button v-if="helper.checkAccess(deletePermission) != -1" @click="permanentDeleteInvoice(item)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-danger btn-xs">
                                                <AppIcon name="bi-x-circle" class="bi bi-x-circle" style="font-size:12px" />
                                                {{$t('delete_permanently')}}
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-if="!loading && trashInvoices.length === 0" class="text-center py-4 text-muted">
                            <AppIcon name="bi-trash" class="bi bi-trash text-muted" style="font-size: 2rem;" />
                            <p class="mt-2 mb-0">{{$t('trash_empty')}}</p>
                        </div>
                        <div v-if="trashCount > trashInvoices.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="trashPagination" :current-page="trashPage" @change="getTrashInvoices" />
                        </div>
                    </div>

                    <div v-if="!showTrash">
                    <!-- table -->
                    <div :data-testid="documentMode === 'process' ? 'process-table-scroll' : 'invoice-table-scroll'" class="table-responsive document-list-table">
                        <table class="table mb-0 table-borderless table-striped small sales-document-table" style="--sales-document-table-min-width: 1350px;">
                            <thead>
                                <tr class="text-secondary">
                                    <th class="pt-4 pb-3 px-6" style="width: 40px;">
                                        <div class="form-check mb-0">
                                            <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                                        </div>
                                    </th>
                                    <th v-for="column in visibleSortableColumns" :key="column.key" class="pt-4 pb-3 px-6" :aria-sort="getAriaSort(column.key)">
                                        <button type="button" class="invoice-sort-button" :aria-label="getSortAriaLabel(column)" @click="sortBy(column.key)">
                                            <span>{{$t(column.label)}}</span>
                                            <AppIcon :name="getSortIcon(column.key)" class="bi ms-1" :class="getSortIcon(column.key)" aria-hidden="true" />
                                        </button>
                                    </th>
                                    <th v-if="isDocumentColumnVisible('action')" class="pt-4 pb-3 px-6 sales-sticky-action-cell">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <span>{{$t('action')}}</span>
                                            <div v-if="role === 'owner' || role === 'admin'" class="dropdown">
                                                <button class="btn btn-sm btn-link p-0 ms-2 text-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                    <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" />
                                                </button>
                                                <ul class="dropdown-menu dropdown-menu-end shadow">
                                                    <li>
                                                        <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="toggleTrashView">
                                                            <AppIcon name="bi-trash" class="bi bi-trash me-2 text-danger" />
                                                            <span>{{ $t('trash') }}</span>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="invoice in invoices"
                                :key="invoice.id"
                                :class="[invoiceReminderClass(invoice), {'table-active': invoice.selected}]"
                            >
                                <td class="py-5 px-6">
                                    <div class="form-check mb-0">
                                        <input class="form-check-input" type="checkbox" v-model="invoice.selected" @change="changeSelectedItem">
                                    </div>
                                </td>
                                <td v-if="isDocumentColumnVisible('reference')" class="py-5 px-6">
                                    <router-link v-if="documentMode !== 'process'" :to="`/invoices/${invoice.id}`"><b>{{invoice.reference}}</b></router-link>
                                    <a v-else href="#" @click.prevent="viewRow(invoice)"><b>{{invoice.reference}}</b></a>
                                    <span
                                        v-if="getInvoiceReminder(invoice.id)"
                                        class="invoice-reminder-badge ms-2"
                                        :class="`invoice-reminder-badge--${reminderUrgency(getInvoiceReminder(invoice.id))}`"
                                        :title="reminderTitle(getInvoiceReminder(invoice.id))"
                                    >
                                        <AppIcon name="bi-bell-fill" class="bi bi-bell-fill me-1" />{{formatReminderRelative(getInvoiceReminder(invoice.id).remind_at)}}
                                    </span>
                                </td>
                                <td v-if="isDocumentColumnVisible('total')" class="py-5 px-6">{{formatInvoiceMoney(calculateItemPrice(invoice), invoice)}} </td>
                                <td v-if="isDocumentColumnVisible('amount_paid')" class="py-5 px-6">{{formatInvoiceMoney(getInvoicePaidAmount(invoice), invoice)}}</td>
                                <td v-if="isDocumentColumnVisible('status')" class="py-5 px-6">
                                    <div v-if="helper.checkAccess(editPermission) != -1 && !isInvoiceStatusDropdownDisabled(invoice)" class="dropdown">
                                        <button
                                            :id="`invoice-status-${invoice.id}`"
                                            type="button"
                                            class="badge border-0 dropdown-toggle"
                                            :class="getInvoiceStatusBadgeClass(invoice.status)"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            :disabled="isInvoiceStatusDropdownDisabled(invoice)"
                                        >
                                            <span v-if="statusUpdatingInvoiceId === invoice.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            {{ $t(invoice.status) }}
                                        </button>
                                        <ul class="dropdown-menu shadow" :aria-labelledby="`invoice-status-${invoice.id}`">
                                            <li v-for="status in getInvoiceStatusOptions(invoice)" :key="status">
                                                <button
                                                    type="button"
                                                    class="dropdown-item d-flex align-items-center justify-content-between gap-3"
                                                    :class="{ active: invoice.status === status }"
                                                    :disabled="isInvoiceStatusOptionDisabled(invoice, status)"
                                                    @click="changeInvoiceStatus(invoice, status)"
                                                >
                                                    <span>{{ $t(status) }}</span>
                                                    <AppIcon name="bi-check-lg" v-if="invoice.status === status" class="bi bi-check-lg" />
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                    <span v-else class="badge" :class="getInvoiceStatusBadgeClass(invoice.status)">
                                        {{ $t(invoice.status) }}
                                    </span>
                                </td>
                                <td v-if="isDocumentColumnVisible('order')" class="py-5 px-6"><router-link v-if="invoice.order && invoice.order.reference" :to="`/orders/${invoice.order.id}`">{{invoice.order && invoice.order.reference ? invoice.order.reference : '-'}}</router-link></td>
                                <td v-if="isDocumentColumnVisible('client') && !hideClient" class="py-5 px-6">
                                    <router-link
                                        v-if="invoice && invoice.client && invoice.client.name"
                                        :to="`/clients/${invoice.client.id}`"
                                        :title="invoice.client.name"
                                        :aria-label="invoice.client.name"
                                    >{{truncateClientName(invoice.client.name)}}</router-link>
                                </td>
                                <td v-if="isDocumentColumnVisible('date')" class="py-5 px-6">{{formatDate(invoice.date)}}</td>
                                <td v-if="isDocumentColumnVisible('due_date')" class="py-5 px-6">
                                    <span v-if="invoice.due_date">
                                        {{ formatDate(invoice.due_date) }}
                                    </span>
                                </td>
                                <td v-if="isDocumentColumnVisible('created_by')" class="py-5 px-6">
                                    {{invoice.user ? invoice.user.name : '-'}}
                                </td>
                                <td v-if="isDocumentColumnVisible('action')" class="py-5 px-6 sales-sticky-action-cell">
                                    <div class="sales-row-actions">
                                        <div
                                            v-if="documentMode !== 'process'
                                                && invoice.invoice_type !== 'credit_note'
                                                && !['draft', 'canceled'].includes(invoice.status)
                                                && helper.checkAccess('create_deliveries') > 0"
                                            class="dropdown d-inline-block"
                                        >
                                            <button
                                                :id="`invoice-transfer-${invoice.id}`"
                                                class="btn btn-primary btn-xs dropdown-toggle"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" style="font-size:12px" /> {{$t('transfert')}}
                                            </button>
                                            <ul class="dropdown-menu dropdown-menu-end" :aria-labelledby="`invoice-transfer-${invoice.id}`">
                                                <li>
                                                    <button type="button" class="dropdown-item" @click="transferDelivery(invoice)">
                                                        <AppIcon name="bi-truck" class="bi bi-truck me-2" />{{$t('delivery')}}
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                        <button v-if="invoice.status != 'draft'" @click="viewRow(invoice)" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                                            {{resolvedDocumentTitle}}
                                        </button>
                                        <button v-if="helper.checkAccess(editPermission) != -1" @click="editRow(invoice)" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                                            {{$t('edit')}}
                                        </button>
                                        <button v-if="helper.checkAccess(deletePermission) != -1 " @click="deleteRow(invoice)" class="btn btn-primary btn-xs">
                                            <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                                            {{$t('delete')}}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="invoices.length" class="sales-total-row" style="background-color: #f8f9fa;font-weight: bold">
                                <td>{{$t('total')}}:</td>
                                <td v-if="isDocumentColumnVisible('reference')"></td>
                                <td v-if="isDocumentColumnVisible('total')" class="py-5 px-6">{{formatCurrencyTotals(invoices, calculateItemPrice)}}</td>
                                <td v-if="isDocumentColumnVisible('amount_paid')" class="py-5 px-6">{{formatCurrencyTotals(invoices, getInvoicePaidAmount)}}</td>
                                <td v-if="isDocumentColumnVisible('status')"></td>
                                <td v-if="isDocumentColumnVisible('order')"></td>
                                <td v-if="isDocumentColumnVisible('client') && !hideClient"></td>
                                <td v-if="isDocumentColumnVisible('date')"></td>
                                <td v-if="isDocumentColumnVisible('due_date')"></td>
                                <td v-if="isDocumentColumnVisible('created_by')"></td>
                                <td v-if="isDocumentColumnVisible('action')" class="sales-sticky-action-cell"></td>
                            </tr>
                            </tbody>
                        </table>

                        <div v-if="count > invoices.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="pagination" :current-page="page" @change="getInvoices" />
                        </div>
                        <table-loading-bar v-if="loading" class="page-table-state document-list-loading" />
                    </div>
                    <!-- table -->
                    <div v-if="loading === false && !invoices.length && !isFiltered" class="page-table-state document-list-empty text-center my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
                        <AppIcon name="bi-receipt" class="bi bi-receipt text-muted" style="font-size: 3rem;" />
                        <h4 class="mt-3">{{ documentMode === 'process' ? $t('no_processes_added') : $t('no_invoices_added') }}</h4>
                        <p class="text-muted">{{ documentMode === 'process' ? $t('start_by_adding_processes') : $t('start_by_adding_invoices') }}</p>
                        <template v-if="helper.checkAccess(createPermission) != -1">
                            <button class="btn btn-outline-primary mt-2" @click="create">
                                {{ documentMode === 'process' ? `${$t('create')} ${resolvedDocumentTitle}` : $t('create_first_invoice') }}
                            </button>
                        </template>
                    </div>
                    <div v-if="loading === false && !invoices.length && isFiltered" class="page-table-state document-list-empty text-center">{{$t('no_invoice_found')}}.</div>

                </div>
                    </div>
                <div v-if="!showTrash" class="col-sm-6">
                    {{$t('show')}}
                    <select v-model="per_page" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    {{$t('entities')}}
                </div>
                </div>
        </section>

        <!-- Bulk Action Bar -->
        <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; justify-content: space-between;">
            <div class="d-flex align-items-center">
                <span class="fw-bold me-2">{{ selectedCount }}</span>
                <span class="text-muted">{{$t('selected')}}</span>
            </div>
            <div class="d-flex gap-2">
                <button v-if="helper.checkAccess(deletePermission) != -1" class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                    <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
                </button>
            </div>
        </div>
    

        <!-- create Modal -->
        <add-edit-invoice-modal 
            v-if="createModalisOpen" 
            @modalClosed="createModalisOpen = false"
            modalId="create-invoice-modal-01" 
            :isModalOpen="createModalisOpen" 
            :data="invoice" 
            action="create"
            :documentMode="documentMode"
            :documentTitle="resolvedDocumentTitle"
            @refresh="refresh"
        ></add-edit-invoice-modal>
        <!-- create modal -->

        <!-- Edit Modal -->
        <add-edit-invoice-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-invoice-modal-01" 
            :isModalOpen="editModalisOpen"  
            :data="invoice" 
            action="update" 
            :documentMode="documentMode"
            :documentTitle="resolvedDocumentTitle"
            @refresh="refresh"
        ></add-edit-invoice-modal>
        <!-- Edit modal -->

        <!-- Status payment Modal -->
        <add-edit-payment-modal
            v-if="statusPaymentModalIsOpen"
            @modalClosed="statusPaymentModalIsOpen = false"
            modalId="invoice-status-payment-modal-01"
            :isModalOpen="statusPaymentModalIsOpen"
            :data="statusPayment"
            action="create"
            @refresh="handleStatusPaymentSaved"
        ></add-edit-payment-modal>
        <!-- Status payment modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-invoice-modal-01" 
            :isModalOpen="invoiceModalisOpen"  
            :data="invoice" 
            :type="documentMode === 'process' ? 'receipt' : documentMode">
        </invoice-template-modal>
        <!-- quote modal -->
        
        <!-- create Modal -->
        <add-edit-credit-invoice-modal 
            v-if="documentMode !== 'process' && createCreditInvoiceModalState"
            @modalClosed="createCreditInvoiceModalState = false"
            modalId="create-credit-invoice-modal-01" 
            :isModalOpen="createCreditInvoiceModalState" 
            :data="invoice" 
            action="transfer"
        ></add-edit-credit-invoice-modal>
        <!-- create modal -->

        <!-- Transfer to delivery modal -->
        <add-edit-delivery-modal
            v-if="createDeliveryModalState"
            @modalClosed="createDeliveryModalState = false"
            modalId="invoice-transfer-delivery-modal-01"
            :isModalOpen="createDeliveryModalState"
            :data="invoice"
            from="invoices"
            action="transfer"
        ></add-edit-delivery-modal>
        <!-- Transfer to delivery modal -->


  </div>
</template>

<script>
import helper from '../helper'
import moment from 'moment'
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable'
// import VueJsonToCsv from 'vue-json-to-csv'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import AddEditInvoiceModal from '../components/invoice/AddEditInvoiceModal';
import AddEditCreditInvoiceModal from '../components/creditInvoice/AddEditInvoiceModal';
import AddEditDeliveryModal from '../components/delivery/AddEditDeliveryModal';
import AddEditPaymentModal from '../components/payment/AddEditPaymentModal.vue';

import InvoiceTemplateModal from '../components/invoiceLayout/InvoiceTemplateModal';
import AutoComplete from '../components/autocomplete/AutoComplete';
import { getSalesDocumentColumnDefaults, getSalesDocumentColumnMetadataKey } from '../constants/salesDocumentColumns';

export default {
  name: 'InvoiceComponent',
  persistedFilterState: {
    key: 'invoices',
    fields: ['activeFilters', 'clientFilterName', 'orderFilterReference', 'dateRangeOption', 'range', 'start', 'end', 'per_page', 'sortField', 'sortDirection'],
    dateFields: ['range'],
    routeQueryFields: ['status', 'start', 'end'],
  },
  components : {
    DatePicker,
    AddEditInvoiceModal,
    AddEditCreditInvoiceModal,
    AddEditDeliveryModal,
    AddEditPaymentModal,
    InvoiceTemplateModal,
    AutoComplete
  },
   props : [
    'client_id',
    'order_id',
    'user_id',
    'inventory_id',
    'product_id',
    'keyword',
    'isDetailClient',
    'products',
    'client',
    'showFilters',
    'documentMode',
    'documentTitle'
    ],
  data(){
    return {
        invoices : [],
        invoiceReminders: {},
        count : 0,
        page : 1,
        showTrash : false,
        trashInvoices: [],
        trashCount: 0,
        trashPage: 1,
        trashPagination: [],
        deliveries : [],
        invoice : {
            id : null,
            reference : `I-00`,
            date : '',
            notes : '',
            products : [], 
            client_id : null,
            client : {},
            payment_method : 'cash',
            status : 'paid',
            rest : 0,
            total : 0,
            amount_paid : 0,
            currency: this.$store.getters.BUSINESS?.currency || '$',
        },
        range: [],
        start : null,
        end : null,
        dateRangeOption: '',
        activeFilters: [],
        availableFilters: [
            { 
                key: 'status', 
                options: [
                    { value: 'pending', label: 'pending' },
                    { value: 'paid', label: 'paid' },
                    { value: 'overdue', label: 'overdue' },
                    { value: 'partially_paid', label: 'partially_paid' },
                    { value: 'canceled', label: 'canceled' }
                ] 
            },
            { 
                key: 'payment_method', 
                options: [] 
            },
            {
                key: 'client',
                options: []
            },
            {
                key: 'order',
                options: []
            },
            {
                key: 'total_amount',
                options: [] // Special case, handled manually
            }
        ],
        totalHtPrice : 0,
        purchasePrice : 0,
        loading : false,
        per_page : 25,
        pagination : [],
        sortField: 'created_at',
        sortDirection: 'desc',
        sortableColumns: [
            { key: 'reference', label: 'reference' },
            { key: 'total', label: 'total' },
            { key: 'amount_paid', label: 'amount_paid' },
            { key: 'status', label: 'status' },
            { key: 'order', label: 'order' },
            { key: 'client', label: 'client' },
            { key: 'date', label: 'date' },
            { key: 'due_date', label: 'due_date' },
            { key: 'created_by', label: 'created_by' }
        ],
        hideClient : false,
        createModal : null,
        editModal : null,
        createCreditInvoiceModalState : false,
        createDeliveryModalState : false,
        invoiceModalisOpen : false,
        createModalisOpen :false,
        editModalisOpen :false,
        statusPaymentModalIsOpen: false,
        statusPayment: null,
        statusPaymentInvoice: null,
        statusPaymentOutstanding: 0,
        statusUpdatingInvoiceId: null,
        invoiceStatusOptions: ['pending', 'partially_paid', 'paid', 'canceled'],
        createClientState : false,
        isSelectedItem: false,
        clientFilterName: '',
        orderFilterReference: ''
    }
  },

    async mounted(){
        // Apply query params from dashboard navigation (status filter + date range)
        const query = this.$route.query;
        let shouldFetch = true;

        if (query.status) {
            // Add status as an active filter
            const statusValue = query.status;
            if (['paid', 'pending', 'partially_paid', 'canceled', 'overdue'].includes(statusValue)) {
                this.activeFilters.push({ key: 'status', value: statusValue });
            }
        }
        if (query.start && query.end) {
            // Set custom date range from query params
            this.dateRangeOption = 'custom';
            this.start = query.start;
            this.end = query.end;
            // Setting range will trigger the watcher which calls getInvoices(1)
            this.range = [new Date(query.start), new Date(query.end)];
            shouldFetch = false;
        } else {
            // Initialize default date range
            this.handleDateRangeChange();
            // If handleDateRangeChange set range, the watcher will trigger getInvoices
            if (this.range && this.range.length === 2 && this.range[0] && this.range[1]) {
                shouldFetch = false;
            }
        }

        if (shouldFetch) {
            this.getInvoices()
        }
        this.fetchPaymentMethods()

         // Check for create param and scanned data from AI
        if(this.$route.query.create === 'true' && this.$route.query.scanned_data) {
            this.loading = true;
            try {
                const scannedData = JSON.parse(this.$route.query.scanned_data);
                const processedData = await this.processAndCreateEntities(scannedData);
                this.openCreateModalWithData(processedData);
                
                // Clean up query params
                const query = Object.assign({}, this.$route.query);
                delete query.create;
                delete query.scanned_data;
                this.$router.replace({ query });
            } finally {
                this.loading = false;
            }
        }
        window.addEventListener('reminders:changed', this.loadInvoiceReminders)
    },
    beforeDestroy(){
        window.removeEventListener('reminders:changed', this.loadInvoiceReminders)
    },
    methods : {
    getInvoiceReminder(invoiceId) {
        return this.invoiceReminders[invoiceId] || null
    },
    reminderUrgency(reminder) {
        if (!reminder || !reminder.remind_at) return 'scheduled'
        const remaining = new Date(reminder.remind_at).getTime() - Date.now()
        if (remaining <= 0) return 'due'
        if (remaining <= 24 * 60 * 60 * 1000) return 'imminent'
        if (remaining <= 3 * 24 * 60 * 60 * 1000) return 'soon'
        return 'scheduled'
    },
    invoiceReminderClass(invoice) {
        if (this.documentMode === 'process') return ''
        const reminder = this.getInvoiceReminder(invoice.id)
        return reminder ? `invoice-reminder-row invoice-reminder-row--${this.reminderUrgency(reminder)}` : ''
    },
    formatReminderRelative(value) {
        return helper.formatDateFromNow(value)
    },
    reminderTitle(reminder) {
        const note = String(reminder.note || '').slice(0, 120)
        return `${this.$t('reminder')}: ${helper.formatDateTime(reminder.remind_at)}${note ? ` — ${note}` : ''}`
    },
    async loadInvoiceReminders() {
        if (this.documentMode === 'process') {
            this.invoiceReminders = {}
            return
        }

        const entityIds = this.invoices.map(invoice => invoice.id)
        if (!entityIds.length) {
            this.invoiceReminders = {}
            return
        }

        try {
            const params = new URLSearchParams({
                entity_type: 'invoice',
                entity_ids: entityIds.join(','),
                status: 'scheduled',
                limit: '500'
            })
            const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders?${params.toString()}`)
            const reminderMap = {}
            for (const reminder of response.data.data || []) {
                if (!reminderMap[reminder.entity_id]) reminderMap[reminder.entity_id] = reminder
            }
            this.invoiceReminders = reminderMap
        } catch (e) {
            this.invoiceReminders = {}
        }
    },
    isDocumentColumnVisible(key) {
        return this.documentColumns[key] !== false
    },
    truncateClientName(name) {
        if (!name) return '-'
        return name.length > 20 ? `${name.slice(0, 20)}...` : name
    },
        parsePrice(priceStr) {
            if (typeof priceStr === 'number') return priceStr;
            if (!priceStr) return 0;
            let clean = priceStr.replace(/[^0-9.]/g, '');
            return parseFloat(clean) || 0;
        },
        async processAndCreateEntities(data) {
            // 1. Client Auto-Creation
            if (data.supplier_name) {
                const clientName = data.supplier_name;
                try {
                    const response = await helper.customGet(`${process.env.VUE_APP_API}/clients?keyword=${encodeURIComponent(clientName)}`);
                    if (response.data && response.data.data && response.data.data.length > 0) {
                        data.existing_client = response.data.data[0];
                    } else {
                        // Create Client
                        const newClient = {
                            name: clientName,
                            phone: '0000000000',
                            notes: 'Auto-created from scan invoice.'
                        };
                        const createResp = await helper.customPost(`${process.env.VUE_APP_API}/clients`, newClient);
                        // Assuming response structure, fallback if needed
                        data.existing_client = createResp.data.client || createResp.data;
                        if (!data.notes) data.notes = '';
                        data.notes += `\nAuto-created client: ${clientName}`;
                    }
                } catch (e) {
                    alert(e)
                }
            }

            // 2. Products Auto-Creation
            if (data.products && Array.isArray(data.products)) {
                const processedProducts = [];
                for (const p of data.products) {
                    const label = p.label || p.name;
                    if (!label) continue;

                    try {
                        const response = await helper.customGet(`${process.env.VUE_APP_API}/products?keyword=${encodeURIComponent(label)}`);
                        if (response.data && response.data.data && response.data.data.length > 0) {
                            // Found existing
                            const existing = response.data.data[0];
                            p.id = existing.id;
                            p.vat = existing.vat;
                            // Prefer existing system price if scanned price is missing/zero?
                            // Or trust scan? Let's trust scan unless zero.
                            const scannedPrice = this.parsePrice(p.price);
                            p.price = scannedPrice > 0 ? scannedPrice : existing.price;
                        } else {
                            // Create Product
                            const newProduct = {
                                label: label,
                                price: this.parsePrice(p.price),
                                purchase_price: 0,
                                vat: 0,
                                quantity: p.quantity || 0,
                                notes: 'Auto-created from scan invoice',
                                has_variants: false,
                                variants: []
                            };
                            const createResp = await helper.customPost(`${process.env.VUE_APP_API}/products`, newProduct);
                            const created = createResp.data.product || createResp.data;
                            p.id = created.id;
                            p.vat = created.vat;
                        }
                    } catch (e) {
                        alert(e)
                    }
                    processedProducts.push(p);
                }
                data.products = processedProducts;
            }
            return data;
        },
        openCreateModalWithData(data){
            this.clearForm()
            this.clearForm()

            if(data.reference) this.invoice.reference = data.reference;
            if(data.date) this.invoice.date = data.date;
            if(data.due_date) this.invoice.due_date = data.due_date;
            if(data.notes) this.invoice.notes = data.notes;
            
            if(data.existing_client) {
                this.client = data.existing_client;
                this.invoice.client = data.existing_client;
                this.invoice.client_id = data.existing_client.id;
            } else if(data.supplier_name && !this.invoice.notes?.includes(data.supplier_name)) {
                 this.invoice.notes = (this.invoice.notes || '') + `\nExtracted Name: ${data.supplier_name}`;
            }

            if(data.products && Array.isArray(data.products)){
                this.invoice.products = data.products.map(p => ({
                    id: p.id || null, // Use ID if we found/created it
                    label: p.label || p.name || 'Unknown Product',
                    quantity: p.quantity || 1,
                    price: this.parsePrice(p.price),
                    discount: 0,
                    vat: this.parsePrice(p.vat) || this.business.vat,
                    total: this.parsePrice(p.total),
                    sub_total: this.parsePrice(p.sub_total) || (p.quantity * this.parsePrice(p.price)) || 0,
                    custom_product: !p.id // If no ID, it's custom
                }));
                 this.invoice.products.push({
                    label : '',
                    id : null,
                    quantity : 0,
                    price : 0,
                    discount : 0,
                    vat : this.business.vat,
                    total : 0,
                    sub_total : 0,
                })
            } else {
                 this.invoice.products.push({
                    label : '',
                    id : null,
                    quantity : 0,
                    price : 0,
                    discount : 0,
                    vat : this.business.vat,
                    total : 0,
                    sub_total : 0,
                })
            }
            this.createModalisOpen = true;
        },
        async create(){

            this.clearForm() 
    
            if(this.client){
                this.invoice.client = this.client
                this.invoice.client_id = this.client.id
            }
    
            if(this.order_id){
                this.invoice.order_id = this.order_id
            }
    
            if(this.products){
                for(let i = 0 ; i < this.products.length ; i++){
                    this.products[i].label = this.products[i].product_label 
                }
                this.invoice.products = this.products
            }
    
            this.invoice.date = moment().format('YYYY-MM-DD')
             this.invoice.products.push({
                label : '',
                id : null,
                quantity : 0,
                price : 0,
                discount : 0,
                vat : this.business.vat,
                total : 0,
                sub_total : 0,
            })
    
            this.createModalisOpen = true;
        },
    editRow(row){
        this.editModalisOpen = true;
        this.clearForm()
        this.invoice = row 
        this.invoice.client_id = row.client && row.client.id ? row.client.id : null
        this.invoice.date = moment(this.invoice.date).format('YYYY-MM-DD')
    },
    viewRow(row){
        this.clearForm()
        this.invoice = row 
        this.invoiceModalisOpen = true;
    },
    async deleteRow(row){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/${this.apiResource}/${row.id}`)
                    this.invoices = helper.withoutDeletedRecords(this.invoices, row.id)
                    this.count = Math.max(0, this.count - 1)
                    this.$toast.open({
                        message: this.documentMode === 'process' ? this.$t('processes_deleted_successfully') : this.$t('invoices_deleted_successfully'),
                        type: 'success',
                        position: 'bottom'
                    });
                }
            })
    },
    clearForm(){
        this.invoice = {
            id : null,
            reference : '',
            date : '',
            products : [],
            payment_method : 'cash',
            status : 'pending',
            rest : 0,
            total : 0,
            amount_paid : 0,
            currency: this.business?.currency || '$',
        }

    },
    async getInvoices(page){
        try{
            this.loading = true
            this.page = page ? page : 1
            
            // Extract filters
            const statusFilter = this.activeFilters.find(f => f.key === 'status');
            const status = statusFilter ? statusFilter.value : 'all';

            const paymentFilter = this.activeFilters.find(f => f.key === 'payment_method');
            const payment_method = paymentFilter ? paymentFilter.value : 'all';

            const clientFilter = this.activeFilters.find(f => f.key === 'client');
            const orderFilter = this.activeFilters.find(f => f.key === 'order');
            const amountFilter = this.activeFilters.find(f => f.key === 'total_amount');

            // Build query params
            const params = new URLSearchParams();
            params.append('page', this.page);
            params.append('per_page', this.per_page);
            params.append('sort_field', this.sortField);
            params.append('sort_type', this.sortDirection);

            if (this.start) {
                params.append('start', this.start);
                params.append('end', this.end);
            }

            if (status !== 'all') params.append('status', status);
            if (payment_method !== 'all') params.append('payment_method', payment_method);
            if (clientFilter && clientFilter.value) params.append('client_id', clientFilter.value);
            if (orderFilter && orderFilter.value) params.append('order_id', orderFilter.value);
            
            if (amountFilter && amountFilter.value !== null && amountFilter.value !== '') {
                params.append('amount_operator', amountFilter.operator);
                params.append('amount_value', amountFilter.value);
            }
            
            // Add optional filters if they exist
            if (this.client_id) params.append('client_id', this.client_id);
            if (this.order_id) params.append('order_id', this.order_id);
            if (this.inventory_id) params.append('inventory_id', this.inventory_id);
            if (this.product_id) params.append('product_id', this.product_id);
            if (this.user_id) params.append('user_id', this.user_id);
            if (this.keyword && this.keyword.length > 2) params.append('keyword', this.keyword);

            let url = `${process.env.VUE_APP_API}/${this.apiResource}?${params.toString()}`;

            const response = await helper.customGet(url)
            this.invoices = response.data.data
            for(let i = 0 ; i < this.invoices.length ; i++){
                this.$set(this.invoices[i], 'selected', false)
            }
            await this.loadInvoiceReminders()
            this.count = response.data.count
            if(this.per_page != response.data.per_page){
                this.per_page = response.data.per_page
            }
            //pagination
            this.pagination = []
            let MaxPagination = Math.ceil(this.count / this.per_page )
            for(let i = 1 ; i <= MaxPagination ;i++){
                this.pagination.push(i)
            }
            this.loading = false
        }catch(error){
            this.loading = false
        }

    },
    sortBy(field) {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }
        this.getInvoices(1);
    },
    getSortIcon(field) {
        if (this.sortField !== field) return 'bi-arrow-down-up';
        return this.sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down';
    },
    getAriaSort(field) {
        if (this.sortField !== field) return 'none';
        return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },
    getSortAriaLabel(column) {
        const nextDirection = this.sortField === column.key && this.sortDirection === 'asc'
            ? 'descending'
            : 'ascending';
        return `${this.$t('sort_by')} ${this.$t(column.label)} (${this.$t(nextDirection)})`;
    },
    getInvoiceStatusBadgeClass(status){
        return {
            'bg-info': status === 'draft',
            'bg-success': status === 'paid',
            'bg-warning': status === 'partially_paid',
            'bg-danger': status === 'canceled' || status === 'overdue',
            'bg-secondary': status === 'pending'
        }
    },
    getInvoiceTotal(invoice){
        const storedTotal = Number(invoice && invoice.total)
        if (Number.isFinite(storedTotal) && storedTotal > 0) return storedTotal

        return invoice && Array.isArray(invoice.products)
            ? invoice.products.reduce((sum, product) => sum + (Number(product.total) || 0), 0)
            : 0
    },
    getInvoicePaidAmount(invoice){
        if (invoice && Array.isArray(invoice.transactions) && invoice.transactions.length) {
            return invoice.transactions
                .filter(transaction => transaction.status === 'paid')
                .reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0)
        }

        const storedAmount = Number(invoice && invoice.amount_paid)
        if (Number.isFinite(storedAmount)) return storedAmount
        return 0
    },
    getInvoiceOutstanding(invoice){
        return Math.max(0, this.getInvoiceTotal(invoice) - this.getInvoicePaidAmount(invoice))
    },
    requiresStatusPayment(invoice, status){
        return ['partially_paid', 'paid'].includes(status) && this.getInvoiceOutstanding(invoice) > 0
    },
    getInvoiceStatusOptions(invoice){
        return invoice.status === 'paid' ? ['canceled'] : this.invoiceStatusOptions
    },
    isInvoiceStatusDropdownDisabled(invoice){
        return this.statusUpdatingInvoiceId === invoice.id
            || invoice.status === 'canceled'
    },
    isInvoiceStatusOptionDisabled(invoice, status){
        if (invoice.status === status) return false
        if (status === 'partially_paid' && this.getInvoiceOutstanding(invoice) <= 0) return true

        return this.requiresStatusPayment(invoice, status)
            && helper.checkAccess('create_payments') === -1
    },
    async changeInvoiceStatus(invoice, status){
        if (this.isInvoiceStatusDropdownDisabled(invoice)
            || invoice.status === status
            || this.isInvoiceStatusOptionDisabled(invoice, status)) return

        if (this.requiresStatusPayment(invoice, status)) {
            this.openStatusPaymentModal(invoice, status)
            return
        }

        await this.updateInvoiceStatus(invoice, status, true)
    },
    openStatusPaymentModal(invoice, status){
        const outstanding = this.getInvoiceOutstanding(invoice)
        const total = this.getInvoiceTotal(invoice)

        this.statusPaymentInvoice = invoice
        this.statusPaymentOutstanding = outstanding
        this.statusPayment = {
            id: null,
            reference: '',
            date: moment().format('YYYY-MM-DD'),
            due_date: invoice.due_date ? moment(invoice.due_date).format('YYYY-MM-DD') : null,
            notes: '',
            client_id: invoice.client_id || (invoice.client && invoice.client.id) || null,
            client: invoice.client || {},
            [this.documentIdField]: invoice.id,
            ...(this.documentMode === 'process' ? {} : { invoice }),
            transactions: invoice.transactions || [],
            payment_method: '',
            status: 'paid',
            total,
            amount_paid: status === 'paid' ? outstanding : 0,
            maximum_amount: outstanding,
            requested_invoice_status: status,
            lock_invoice_payment: true,
            lock_payment_amount: status === 'paid',
            hideTypeToggle: true,
            is_incoming: true,
            paymentable_type: this.documentMode,
            paymentable_id: invoice.id
        }
        this.statusPaymentModalIsOpen = true
    },
    async handleStatusPaymentSaved(payment){
        const invoice = this.statusPaymentInvoice
        if (!invoice) return

        const paidNow = Number(payment && payment.amountPaid) || 0
        const status = paidNow >= this.statusPaymentOutstanding ? 'paid' : 'partially_paid'
        await this.updateInvoiceStatus(invoice, status, false)

        this.statusPaymentInvoice = null
        this.statusPayment = null
        this.statusPaymentOutstanding = 0
    },
    async updateInvoiceStatus(invoice, status, showToast){
        const previousStatus = invoice.status
        this.statusUpdatingInvoiceId = invoice.id
        this.$set(invoice, 'status', status)

        try{
            await helper.customPatch(
                `${process.env.VUE_APP_API}/${this.apiResource}/${invoice.id}`,
                { status },
                { showToast }
            )
        }catch(error){
            this.$set(invoice, 'status', previousStatus)
        }finally{
            await this.getInvoices(this.page)
            this.statusUpdatingInvoiceId = null
        }
    },
    getStatus(status){
       if(status == 'paid'){
         return 'badge bg-success rounded-pill small'
       }else if(status == 'partially_paid'){
         return 'badge bg-success rounded-pill small'
       }else if(status == 'pending'){
         return 'badge bg-primary rounded-pill small'
       }else if(status == 'overdue'){
         return 'badge bg-danger rounded-pill small'
       }else if(status == 'refunded'){
         return 'badge bg-info rounded-pill small'
       }
    },
    async transferCreditNote(invoice){
 
        invoice.total = invoice.products && invoice.products.length ? invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;

        this.invoice = invoice
        this.createCreditInvoiceModalState = !this.createCreditInvoiceModalState

    },
    transferDelivery(invoice){
        invoice.total = invoice.products && invoice.products.length
            ? invoice.products.reduce((total, product) => total + (Number(product.total) || 0), 0)
            : 0;
        this.invoice = invoice;
        this.createDeliveryModalState = true;
    },
    generatePDF() {
        const doc = new jsPDF();

        const data = []
        for(let i = 0 ; i < this.invoices.length ; i++){
            const invoice = this.invoices[i]
            
            data.push([
                invoice.reference,
                `${this.formatInvoiceMoney(this.calculateItemPrice(invoice), invoice)}`,
                `${this.formatInvoiceMoney(this.getInvoicePaidAmount(invoice), invoice)}`,
                this.$t(invoice.status),
                this.$t(invoice.payment_method),
                invoice.client && invoice.client.name ? invoice.client.name : '-',
                this.formatDate(invoice.date)
            ])
        }
        doc.text(this.resolvedDocumentTitle, 15, 10);
        autoTable(doc,
        {
            head: [["Id", "Total(ttc)", "Total donnée", "Status", "Mode de paiment","Commande", "Client","Date"]],
            body: data
        })
        doc.save(this.documentMode === 'process' ? 'processes.pdf' : 'factures.pdf')
    },
    generateCsv(){
        const data = []
        for(let i = 0 ; i < this.orders.length ; i++){
            const order = this.orders[i]
            data.push({
                "Id" : order.id,
                "Total(ttc)" : `${this.formatMoney(this.calculateItemPrice(order))}` ,
                "Total Donnée" : `${this.formatMoney(order.amount_paid)}`,
                "Rest" : `${this.formatMoney(order.total - order.amount_paid)}`,
                "Status" : this.$t(order.status),
                "Mode de paiment" : this.$t(order.payment_method),
                "Client" : order.client && order.client.name ? order.client.name : '-',
                "Date" : this.formatDate(order.date)
            })
        }

        return data
    },
    calculateItemPrice(order){
        if(order.status == "draft") return 0
        return order.products && order.products.length ? order.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0; 
    },
    createCreditInvoiceModal(data){
        this.createCreditInvoiceModalState = data
    },
    refresh(){
        this.getInvoices()
    },
    //helper functions
    formatDate(date,locale) { return helper.formatDate(date,locale) },
    formatMoney(number) { return helper.formatMoney(number);},
    formatInvoiceMoney(number, invoice) {
        return helper.formatMoney(number, invoice?.currency || this.business?.currency)
    },
    formatCurrencyTotals(invoices, valueGetter) {
        const totals = invoices.reduce((result, invoice) => {
            const currency = invoice?.currency || this.business?.currency || '$'
            result[currency] = (result[currency] || 0) + (Number(valueGetter(invoice)) || 0)
            return result
        }, {})

        return Object.entries(totals)
            .map(([currency, total]) => helper.formatMoney(total, currency))
            .join(' / ')
    },
    formatReference(number,id) { return helper.formatReference(number,id);},
    // Handle date range selection
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = []
        this.start = null
        this.end = null
      } else if (this.dateRangeOption === 'today') {
        const start = moment().toDate()
        const end = moment().toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'yesterday') {
        const start = moment().subtract(1, 'days').toDate()
        const end = moment().subtract(1, 'days').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_week') {
        const start = moment().startOf('isoWeek').toDate()
        const end = moment().endOf('isoWeek').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'last_week') {
        const start = moment().subtract(1, 'weeks').startOf('isoWeek').toDate()
        const end = moment().subtract(1, 'weeks').endOf('isoWeek').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_month') {
        const start = moment().startOf('month').toDate()
        const end = moment().endOf('month').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_year') {
        const start = moment().startOf('year').toDate()
        const end = moment().endOf('year').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'last_year') {
        const start = moment().subtract(1, 'years').startOf('year').toDate()
        const end = moment().subtract(1, 'years').endOf('year').toDate()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'custom') {
        this.range = []
      }
    },
    changeSelectedItem(){
        this.isSelectedItem = this.invoices.find(item => item.selected === true ) ? true : false
    },
    toggleSelectAll() {
      const newState = !this.areAllSelected;
      this.invoices.forEach(item => item.selected = newState);
      this.changeSelectedItem();
    },
    async deleteSelected() {
      const selectedIds = this.invoices.filter(item => item.selected).map(item => item.id);
      if (selectedIds.length === 0) return;

      this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      }).then(async (result) => {
        if (result.isConfirmed) {
            this.loading = true;
            try {
                const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/${this.apiResource}/${id}`)));
                const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
                this.invoices = helper.withoutDeletedRecords(this.invoices, deletedIds);
                this.count = Math.max(0, this.count - deletedIds.length);
                if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
                this.isSelectedItem = false;
                this.$toast.open({
                    message: this.documentMode === 'process' ? this.$t('processes_deleted_successfully') : this.$t('invoices_deleted_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (error) {
                this.$toast.open({
                    message: this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            } finally {
                this.loading = false;
            }
        }
      });
    },
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            if (filter.key === 'total_amount') {
                 this.activeFilters.push({
                    key: filter.key,
                    operator: 'gt',
                    value: 0
                });
            } else if (filter.key === 'client' || filter.key === 'order') {
                if (filter.key === 'client') this.clientFilterName = '';
                if (filter.key === 'order') this.orderFilterReference = '';
                this.activeFilters.push({
                    key: filter.key,
                    value: null
                });
            } else {
                // Default select the first option
                const defaultVal = filter.options[0].value;
                this.activeFilters.push({ 
                    key: filter.key, 
                    value: defaultVal 
                });
            }
            // Removed immediate fetch
        }
    },
    removeFilter(index) {
        const filter = this.activeFilters[index];
        if (filter && filter.key === 'client') {
            this.clientFilterName = '';
        }
        if (filter && filter.key === 'order') {
            this.orderFilterReference = '';
        }
        this.activeFilters.splice(index, 1);
        // Removed immediate fetch
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    getFilterOptions(key) {
        const filter = this.availableFilters.find(f => f.key === key);
        return filter ? filter.options : [];
    },
    applyFilters() {
        this.getInvoices();
    },
    clearAllFilters() {
        this.activeFilters = [];
        this.clientFilterName = '';
        this.orderFilterReference = '';
        this.getInvoices();
    },
    async fetchPaymentMethods() {
        try {
            const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
            const methods = response.data.data.filter(m => m.is_active);
            
            // Find the payment_method filter config
            const filterConfig = this.availableFilters.find(f => f.key === 'payment_method');
            if (filterConfig) {
                filterConfig.options = methods.map(m => ({
                    value: m.name.toLowerCase(),
                    label: m.name
                }));
            }
        } catch (error) {
            alert(error)
        }
    },
    emitAutoCompleteClientFilter(data) {
        const clientFilter = this.activeFilters.find(f => f.key === 'client');
        if (data.clear_input) {
            if (clientFilter) {
                clientFilter.value = null;
            }
            this.clientFilterName = '';
            return;
        }
        if (data.result && clientFilter) {
            clientFilter.value = data.result.id;
            this.clientFilterName = data.result.name;
        }
    },
    emitAutoCompleteOrderFilter(data) {
        const orderFilter = this.activeFilters.find(f => f.key === 'order');
        if (data.clear_input) {
            if (orderFilter) {
                orderFilter.value = null;
            }
            this.orderFilterReference = '';
            return;
        }
        if (data.result && orderFilter) {
            orderFilter.value = data.result.id;
            this.orderFilterReference = data.result.reference;
        }
    },
    async toggleTrashView() {
        this.showTrash = !this.showTrash;
        if (this.showTrash) {
            this.trashPage = 1;
            await this.getTrashInvoices(1);
        }
    },
    async getTrashInvoices(page) {
        try {
            this.loading = true;
            this.trashPage = page ? page : 1;
            const url = `${process.env.VUE_APP_API}/${this.apiResource}/trash?page=${this.trashPage}&per_page=50`;
            const response = await helper.customGet(url);
            this.trashInvoices = response.data.data;
            this.trashCount = response.data.count;
            
            this.trashPagination = [];
            let MaxPagination = Math.ceil(this.trashCount / 50);
            for(let i = 1; i <= MaxPagination; i++) {
                this.trashPagination.push(i);
            }
        } catch (error) {
            helper.handleError(error);
        } finally {
            this.loading = false;
        }
    },
    async permanentDeleteInvoice(item) {
        this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text: this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: this.$t('yes_delete_it')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await helper.customDelete(`${process.env.VUE_APP_API}/${this.apiResource}/${item.id}/permanent`);
                    this.trashInvoices = helper.withoutDeletedRecords(this.trashInvoices, item.id);
                    this.$toast.open({
                        message: this.documentMode === 'process' ? this.$t('processes_deleted_successfully') : this.$t('invoices_deleted_successfully'),
                        type: 'success',
                        position: 'bottom'
                    });
                } catch (error) {
                    helper.handleError(error);
                }
            }
        });
    }
  },
  watch: {
    range(val) {
        if (val && val.length === 2 && val[0] && val[1]) {
            this.start = moment(val[0]).format('YYYY-MM-DD')
            this.end = moment(val[1]).format('YYYY-MM-DD')
            this.getInvoices(1)
        } else {
            this.start = null
            this.end = null
            this.getInvoices(1)
        }
    },
    keyword() {
        if (this.keyword.length > 2 || this.keyword === '') {
            this.getInvoices(1)
        }
    }
  },
  computed : {
    apiResource(){ return this.documentMode === 'process' ? 'processes' : 'invoices' },
    permissionResource(){ return this.documentMode === 'process' ? 'receipts' : 'invoices' },
    createPermission(){ return `create_${this.permissionResource}` },
    editPermission(){ return `edit_${this.permissionResource}` },
    deletePermission(){ return `delete_${this.permissionResource}` },
    documentIdField(){ return this.documentMode === 'process' ? 'process_id' : 'invoice_id' },
    resolvedDocumentTitle(){ return this.documentTitle || this.$t('invoice') },
    visibleSortableColumns() {
        return this.sortableColumns.filter(column => this.isDocumentColumnVisible(column.key)
            && (column.key !== 'client' || !this.hideClient));
    },
    documentColumns() {
        return {
            ...getSalesDocumentColumnDefaults('invoice'),
            ...(this.business?.metadata?.[getSalesDocumentColumnMetadataKey('invoice')] || {})
        }
    },
    selectableFilters() {
        return this.availableFilters.filter(f => !this.isFilterActive(f.key));
    },
    isFiltered() {
      return (this.activeFilters.length > 0) || 
             (this.dateRangeOption && this.dateRangeOption !== '') ||
             (this.keyword && this.keyword.length > 0) ||
             (this.range && this.range.length > 0);
    },
    isOwner(){ return ['owner', 'admin'].includes(this.$store.getters.ROLE) },
    role(){ return this.$store.getters.ROLE },
    helper(){ return helper },
    permissions(){ return this.$store.getters.PERMISSIONS },
    business(){ return this.$store.getters.BUSINESS},
    total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
    sub_total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0},
    selectedCount() {
      return this.invoices.filter(item => item.selected).length;
    },
    areAllSelected() {
      return this.invoices.length > 0 && this.invoices.every(item => item.selected);
    }
  }
}
</script>

<style scoped>
    .document-list-table {
        min-height: 70vh;
        position: relative;
    }

    .document-list-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
    }

    .document-list-empty {
        min-height: 70vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }

    .invoice-sort-button {
        display: inline-flex;
        align-items: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        font-weight: inherit;
        white-space: nowrap;
    }

    .invoice-sort-button:hover,
    .invoice-sort-button:focus-visible {
        color: var(--bs-primary);
    }

    .invoice-reminder-row > td {
        transition: background-color 0.2s ease, box-shadow 0.2s ease;
    }

    .invoice-reminder-row > td:first-child {
        box-shadow: inset 4px 0 0 #6c8cff;
    }

    .invoice-reminder-row--scheduled > td {
        background-color: #f3f6ff !important;
    }

    .invoice-reminder-row--soon > td {
        background-color: #fff8db !important;
    }

    .invoice-reminder-row--soon > td:first-child {
        box-shadow: inset 4px 0 0 #e0a800;
    }

    .invoice-reminder-row--imminent > td {
        background-color: #fff0df !important;
    }

    .invoice-reminder-row--imminent > td:first-child {
        box-shadow: inset 4px 0 0 #fd7e14;
    }

    .invoice-reminder-row--due > td {
        background-color: #ffe8eb !important;
    }

    .invoice-reminder-row--due > td:first-child {
        box-shadow: inset 4px 0 0 #dc3545;
    }

    .invoice-reminder-badge {
        display: inline-flex;
        align-items: center;
        max-width: 180px;
        padding: 3px 7px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
        vertical-align: middle;
    }

    .invoice-reminder-badge--scheduled {
        color: #3856b8;
        background: #e4eaff;
    }

    .invoice-reminder-badge--soon {
        color: #856404;
        background: #ffe69c;
    }

    .invoice-reminder-badge--imminent {
        color: #a44800;
        background: #ffd8ad;
    }

    .invoice-reminder-badge--due {
        color: #a71d2a;
        background: #ffc9cf;
    }
</style>
